import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../api';
import { toast } from 'sonner';

const categoryEmojis = {
  'Fidgets': '🫧',
  'Squishes': '🐰',
  'Novelty Items': '🌟',
};

const categoryDescriptions = {
  'Fidgets': 'Pop-its, spinners, cubes & more',
  'Squishes': 'Slow-rise, scented & adorable',
  'Novelty Items': 'Slimes, kits & surprise packs',
};

export const ProductCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section className="py-20 px-[5%]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-lg" style={{ color: 'var(--mid)' }}>
            Loading categories...
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="categories" className="py-20 px-[5%]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span
            className="inline-block font-extrabold text-xs tracking-[2px] uppercase mb-[10px]"
            style={{ color: 'var(--pink-d)' }}
          >
            Browse By
          </span>
          <h2
            className="fredoka mb-2"
            style={{
              fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
              color: 'var(--dark)',
            }}
          >
            What Are You Looking For? 🔍
          </h2>
          <p
            className="text-base leading-relaxed max-w-[520px] mx-auto"
            style={{ color: 'var(--mid)' }}
          >
            From pop-its to squishy animals — we've got the toy that'll become their new obsession.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {categories.map((category, index) => {
            const emoji = categoryEmojis[category.name] || '🎁';
            const description = categoryDescriptions[category.name] || category.description;
            
            return (
              <button
                key={category.id}
                onClick={scrollToProducts}
                className="group relative overflow-hidden rounded-[20px] p-8 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg animate-[fadeIn_0.5s_both]"
                style={{
                  background: 'white',
                  boxShadow: 'var(--shadow-sm)',
                  animationDelay: `${index * 100}ms`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
              >
                {/* Emoji Icon */}
                <div
                  className="text-6xl mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12"
                >
                  {emoji}
                </div>

                {/* Category Name */}
                <h3
                  className="fredoka text-2xl mb-2"
                  style={{ color: 'var(--dark)' }}
                >
                  {category.name}
                </h3>

                {/* Description */}
                <p
                  className="text-sm mb-4"
                  style={{ color: 'var(--mid)' }}
                >
                  {description}
                </p>

                {/* Hover Arrow */}
                <div
                  className="text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ color: 'var(--pink-d)' }}
                >
                  View Products →
                </div>
              </button>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-10">
          <button
            onClick={scrollToProducts}
            className="btn-kawaii btn-kawaii-primary font-extrabold"
          >
            Browse All Products →
          </button>
        </div>
      </div>
    </section>
  );
};
